import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/Button'
import { TextInput } from '../ui/TextInput'
import { Textarea } from '../ui/Textarea'
import { Select } from '../ui/Select'
import { contactService, ContactType } from '../../services/contactService'
import { notifications } from '../../utils/notifications'

interface ContactDialogProps {
  opened: boolean
  onClose: () => void
}

const TYPES: ContactType[] = ['bug', 'question', 'feature', 'other']

export function ContactDialog({ opened, onClose }: ContactDialogProps) {
  const { t } = useTranslation('contact')
  const [type, setType] = useState<ContactType>('bug')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const reset = () => {
    setType('bug')
    setSubject('')
    setBody('')
    setSubmitting(false)
  }

  const handleClose = () => {
    if (submitting) return
    reset()
    onClose()
  }

  const handleSubmit = async () => {
    const trimmedSubject = subject.trim()
    const trimmedBody = body.trim()
    if (!trimmedSubject || !trimmedBody) return

    setSubmitting(true)
    try {
      await contactService.submit({
        type,
        subject: trimmedSubject,
        body: trimmedBody,
        pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
      })
      notifications.show({
        title: t('toast.success.title'),
        message: t('toast.success.message'),
        color: 'green',
      })
      reset()
      onClose()
    } catch (error) {
      notifications.show({
        title: t('toast.error.title'),
        message: t('toast.error.message'),
        color: 'red',
      })
      setSubmitting(false)
    }
  }

  const canSubmit = subject.trim().length > 0 && body.trim().length > 0 && !submitting

  return (
    <Dialog open={opened} onOpenChange={(open) => { if (!open) handleClose() }}>
      <DialogContent className="flex max-w-md flex-col gap-0 overflow-hidden p-0 max-h-[calc(100vh-32px)] md:min-h-[80vh] md:max-h-[calc(100vh-64px)]">
        <DialogHeader className="flex-shrink-0 border-b px-6 py-4 text-left">
          <DialogTitle className="pr-8 text-base font-medium leading-normal tracking-normal">
            {t('title')}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="flex flex-col gap-4">
            <p className="text-13 text-muted-foreground">{t('description')}</p>

            <Select
              label={t('fields.type.label')}
              value={type}
              onValueChange={(v) => setType(v as ContactType)}
              options={TYPES.map((value) => ({
                value,
                label: t(`fields.type.options.${value}`),
              }))}
            />

            <TextInput
              label={t('fields.subject.label')}
              placeholder={t('fields.subject.placeholder')}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
              required
            />

            <Textarea
              label={t('fields.body.label')}
              placeholder={t('fields.body.placeholder')}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              minRows={5}
              maxLength={5000}
              showCount
              required
            />
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 gap-2 border-t border-border/50 bg-background px-6 py-4 sm:space-x-0">
          <Button variant="ghost" onClick={handleClose} disabled={submitting}>
            {t('actions.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? t('actions.sending') : t('actions.send')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
